import { VideoPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { useStation } from '@app/core/stations/hooks/useStation';
import { isDiarizationFragmentsEnabled } from '@app/modules/fragments/helpers/is-transcription-enabled';
import { useFragmentMediaUrlQuery } from '@app/modules/fragments/hooks/useFragmentMediaUrlQuery';
import { toTimestamp } from '@mi-front/core';
import { Box } from '@mui/material';
import { QueryView } from '@ui/utils/async-view';
import { ReactNode } from 'react';
import { useFragmentCtx } from '../../../context/fragment-ctx';
import { TranscriptionView } from '../../transcription/TranscriptionView';
import { PlaybackVideoJsPlayer } from './PlaybackVideoJsPlayer';

/**
 * Renders only the video player (no transcription). Must be used inside VideoPlaybackProvider.
 * Used when the parent supplies a custom layout (e.g. transcription in a full-width row below).
 */
export function VideoFragmentPlayerContent() {
  return (
    <Box
      className="test"
      sx={{
        p: 2,
        width: {
          lg: '100%',
          xl: '90%',
        },
        margin: '0 auto',
      }}
    >
      <PlaybackVideoJsPlayer bordered />
    </Box>
  );
}

/**
 * Main component to display the video player for a fragment.
 * Uses VideoPlaybackProvider to manage the player state.
 * When children are provided, renders the provider and children only (no default player/transcription).
 * When no children, renders player and transcription below it.
 */
export const VideoFragmentMedia = (props: { children?: ReactNode }) => {
  const { children } = props;
  const fragment = useFragmentCtx();
  const station = useStation(fragment.stationId);
  const startTs = toTimestamp(fragment.from);
  const stopTs = toTimestamp(fragment.to);

  const content = children ?? (
    <>
      <VideoFragmentPlayerContent />
      <div style={{ marginTop: '1rem' }}>
        {isDiarizationFragmentsEnabled(station) ? (
          <TranscriptionView fragmentId={fragment.id} />
        ) : null}
      </div>
    </>
  );

  return (
    <QueryView query={useFragmentMediaUrlQuery(fragment.id)}>
      {(data) => (
        <VideoPlaybackProvider src={data} playbackRange={{ from: startTs, to: stopTs }}>
          {content}
        </VideoPlaybackProvider>
      )}
    </QueryView>
  );
};
